import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-hot-toast';

const usePendingUsers = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all pending users
  const fetchPendingUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching pending users...');
      
      // Query for all users (without orderBy to avoid potential field issues)
      const q = collection(db, 'users');
      const snapshot = await getDocs(q);
      
      console.log('Total users fetched:', snapshot.docs.length);
      
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('Users data:', usersData);

      // Filter for pending users (handle both array and string role formats)
      const pendingUsersData = usersData.filter(user => {
        if (Array.isArray(user.role)) {
          return user.role.includes('pending');
        }
        return user.role === 'pending';
      });

      console.log('Pending users filtered:', pendingUsersData);
      console.log('Pending users count:', pendingUsersData.length);

      setPendingUsers(pendingUsersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setError('Failed to fetch pending users');
      toast.error('Failed to fetch pending users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update user role (remove pending and add new role)
  const updateUserRole = useCallback(async (userId, newRole) => {
    try {
      setLoading(true);
      const userRef = doc(db, 'users', userId);
      
      // Get current user data to update role array
      const currentUser = pendingUsers.find(user => user.id === userId);
      if (!currentUser) {
        throw new Error('User not found');
      }

      // Remove 'pending' from role array and add new role
      let updatedRoles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role];
      updatedRoles = updatedRoles.filter(role => role !== 'pending');
      updatedRoles.push(newRole);

      const updatesToSave = {
        role: updatedRoles,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(userRef, updatesToSave);
      
      // Refresh the pending users list
      await fetchPendingUsers();
      
      toast.success(`User role updated to ${newRole}`);
      return { success: true };
    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to update user role');
      toast.error('Failed to update user role');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [pendingUsers, fetchPendingUsers]);

  // Bulk update user roles
  const bulkUpdateUserRoles = useCallback(async (userIds, newRole) => {
    try {
      setLoading(true);
      
      const updatePromises = userIds.map(async (userId) => {
        const currentUser = pendingUsers.find(user => user.id === userId);
        if (!currentUser) return;

        let updatedRoles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role];
        updatedRoles = updatedRoles.filter(role => role !== 'pending');
        updatedRoles.push(newRole);

        const userRef = doc(db, 'users', userId);
        return updateDoc(userRef, {
          role: updatedRoles,
          updatedAt: new Date().toISOString()
        });
      });

      await Promise.all(updatePromises);
      
      // Refresh the pending users list
      await fetchPendingUsers();
      
      toast.success(`${userIds.length} users updated to ${newRole}`);
      return { success: true };
    } catch (err) {
      console.error('Error bulk updating user roles:', err);
      setError('Failed to bulk update user roles');
      toast.error('Failed to bulk update user roles');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [pendingUsers, fetchPendingUsers]);

  // Refresh pending users
  const refreshPendingUsers = useCallback(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  // Initial fetch
  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  return {
    // State
    pendingUsers,
    loading,
    error,
    
    // Actions
    updateUserRole,
    bulkUpdateUserRoles,
    refreshPendingUsers,
    
    // Utilities
    totalCount: pendingUsers.length
  };
};

export default usePendingUsers;
